import { Controller } from "@hotwired/stimulus"

// The parts of <model-viewer> this controller uses
interface ModelViewerElement extends HTMLElement {
  src: string | null
  readonly canActivateAR: boolean
  activateAR(): Promise<void>
}

type ModelViewerEvent = CustomEvent<{ type?: string, status?: string }>

// Vue AR d'une œuvre (oeuvres/ar) : charge <model-viewer>, puis le modèle
// glb / usdz servi par oeuvres#modele, et déclenche la réalité augmentée.
export default class extends Controller {
  static targets = ["viewer", "busy", "button", "hint"]
  static values  = { glb: String, usdz: String }

  declare readonly viewerTarget: ModelViewerElement
  declare readonly busyTarget: HTMLElement
  declare readonly buttonTarget: HTMLButtonElement
  declare readonly hintTarget: HTMLElement
  declare readonly glbValue: string
  declare readonly usdzValue: string

  private _timer?: ReturnType<typeof setTimeout>
  private _onLoad!: () => void
  private _onError!: (e: Event) => void
  private _onStatus!: (e: Event) => void

  async connect() {
    const mv = this.viewerTarget
    this._timer = setTimeout(() => this._fail("L'œuvre met trop de temps à charger. Vérifiez votre connexion puis rechargez la page."), 20000)

    this._onLoad = () => {
      clearTimeout(this._timer)
      this.busyTarget.hidden = true
      if (mv.canActivateAR) {
        this.buttonTarget.disabled = false
      } else {
        this.buttonTarget.textContent = "Réalité augmentée indisponible sur cet appareil"
        this.hintTarget.textContent   = "Ouvrez cette page sur un iPhone (Safari) ou un Android compatible ARCore (Chrome)."
      }
    }
    this._onError  = (e) => { clearTimeout(this._timer); this._fail(`L'œuvre n'a pas pu être chargée (${(e as ModelViewerEvent).detail?.type || "erreur"}). Rechargez la page.`) }
    this._onStatus = (e) => {
      if ((e as ModelViewerEvent).detail.status === "failed") this.hintTarget.textContent = "La réalité augmentée n'a pas pu démarrer. Ouvrez la page directement dans Safari ou Chrome, puis réessayez."
    }
    mv.addEventListener("load", this._onLoad)
    mv.addEventListener("error", this._onError)
    mv.addEventListener("ar-status", this._onStatus)

    try {
      await import("@google/model-viewer/dist/model-viewer.min.js")
    } catch {
      clearTimeout(this._timer)
      return this._fail("Le visualiseur n'a pas pu être chargé. Vérifiez votre connexion puis rechargez la page.")
    }
    mv.setAttribute("ios-src", this.usdzValue)
    mv.src = this.glbValue
  }

  disconnect() {
    clearTimeout(this._timer)
    const mv = this.viewerTarget
    mv.removeEventListener("load", this._onLoad)
    mv.removeEventListener("error", this._onError)
    mv.removeEventListener("ar-status", this._onStatus)
  }

  activate() {
    const mv = this.viewerTarget
    if (mv.canActivateAR) mv.activateAR()
  }

  _fail(message: string) {
    this.busyTarget.hidden = false
    this.busyTarget.textContent = message
  }
}
